import React from 'react';
import { 
  Phone 
} from '@mui/icons-material';

const Admissions = () => {
  return (
    <section id="admissions" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-blue-900 mb-4">Admissions</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">Join our vibrant school community. Learn about our admission process, requirements, and deadlines.</p>
        </div>

        <div className="flex flex-col gap-16">
          <div>
            <h3 className="text-blue-800 text-2xl font-semibold mb-8 text-center">Admission Process</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-md text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-blue-800 text-white text-2xl font-bold mb-4">1</div>
                <h4 className="text-blue-900 font-semibold mb-2">Application</h4>
                <p className="text-gray-600 text-sm">Submit your application form online or at our admissions office.</p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-md text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-blue-800 text-white text-2xl font-bold mb-4">2</div>
                <h4 className="text-blue-900 font-semibold mb-2">Assessment</h4>
                <p className="text-gray-600 text-sm">Attend an entrance assessment and interview with our faculty.</p>
              </div>
              <div className="bg-white p-8 rounded-xl shadow-md text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-blue-800 text-white text-2xl font-bold mb-4">3</div>
                <h4 className="text-blue-900 font-semibold mb-2">Enrollment</h4>
                <p className="text-gray-600 text-sm">Complete enrollment and join our school community.</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-blue-800 text-2xl font-semibold mb-8 text-center">Eligibility Requirements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-10 rounded-xl shadow-md text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="text-4xl mb-6 text-blue-700"><Phone fontSize="inherit" /></div>
                <h4 className="text-blue-800 mb-4 text-xl font-semibold">General Requirements</h4>
                <ul className="list-none p-0 m-0 space-y-2">
                  <li className="relative pl-6 text-gray-600 before:content-['•'] before:absolute before:left-0 before:text-blue-800 before:font-bold">Completed application form</li>
                  <li className="relative pl-6 text-gray-600 before:content-['•'] before:absolute before:left-0 before:text-blue-800 before:font-bold">Birth certificate or proof of age</li>
                  <li className="relative pl-6 text-gray-600 before:content-['•'] before:absolute before:left-0 before:text-blue-800 before:font-bold">Previous school records (if applicable)</li>
                  <li className="relative pl-6 text-gray-600 before:content-['•'] before:absolute before:left-0 before:text-blue-800 before:font-bold">Passport-size photographs</li>
                </ul>
              </div>
              <div className="bg-white p-10 rounded-xl shadow-md text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="text-4xl mb-6 text-blue-700"><Phone fontSize="inherit" /></div>
                <h4 className="text-blue-800 mb-4 text-xl font-semibold">Age Criteria</h4>
                <ul className="list-none p-0 m-0 space-y-2">
                  <li className="relative pl-6 text-gray-600 before:content-['•'] before:absolute before:left-0 before:text-blue-800 before:font-bold">Kindergarten: 3+ years</li>
                  <li className="relative pl-6 text-gray-600 before:content-['•'] before:absolute before:left-0 before:text-blue-800 before:font-bold">Primary: 6+ years</li>
                  <li className="relative pl-6 text-gray-600 before:content-['•'] before:absolute before:left-0 before:text-blue-800 before:font-bold">Middle & High School: 11+ years</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-blue-800 text-2xl font-semibold mb-8 text-center">Application Forms</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-10 rounded-xl shadow-md text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="text-4xl mb-6 text-blue-700"><Phone fontSize="inherit" /></div>
                <h4 className="text-blue-800 mb-4 text-xl font-semibold">Online Application</h4>
                <p className="text-gray-600 leading-relaxed mb-4">Fill out our secure online application form and submit required documents electronically.</p>
                <button className="inline-block px-6 py-2 rounded-lg font-medium text-white bg-blue-800 hover:bg-blue-900 transition-all shadow-md">Apply Online</button>
              </div>
              <div className="bg-white p-10 rounded-xl shadow-md text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
                <div className="text-4xl mb-6 text-blue-700"><Phone fontSize="inherit" /></div>
                <h4 className="text-blue-800 mb-4 text-xl font-semibold">Download Form</h4>
                <p className="text-gray-600 leading-relaxed mb-4">Download the application form, fill it out, and submit it at our admissions office.</p>
                <button className="inline-block px-6 py-2 rounded-lg font-medium text-blue-800 border-2 border-blue-800 bg-white hover:bg-blue-800 hover:text-white transition-all shadow-md">Download PDF</button>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-blue-800 text-2xl font-semibold mb-8 text-center">Fee Structure</h3>
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
              <div className="grid grid-cols-3 bg-blue-800 text-white p-6 font-semibold">
                <div>Grade</div>
                <div>Annual Fee</div>
                <div>Details</div>
              </div>
              <div className="divide-y divide-gray-200">
                <div className="grid grid-cols-3 p-6 hover:bg-gray-50">
                  <div className="font-semibold text-blue-900">Kindergarten</div>
                  <div className="font-bold text-blue-800 text-lg">₹ 10,000</div>
                  <div className="text-gray-600 text-sm">Includes books & activities</div>
                </div>
                <div className="grid grid-cols-3 p-6 hover:bg-gray-50">
                  <div className="font-semibold text-blue-900">Primary</div>
                  <div className="font-bold text-blue-800 text-lg">₹ 15,000</div>
                  <div className="text-gray-600 text-sm">Includes books, activities, and field trips</div>
                </div>
                <div className="grid grid-cols-3 p-6 hover:bg-gray-50">
                  <div className="font-semibold text-blue-900">Middle & High School</div>
                  <div className="font-bold text-blue-800 text-lg">₹ 20,000</div>
                  <div className="text-gray-600 text-sm">Includes books, activities, field trips, and exam fees</div>
                </div>
              </div>
            </div>
            <div className="text-center text-gray-500 italic">* Fees are subject to change. Scholarships and financial aid are available.</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Admissions; 